import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  Plus, 
  Trash2, 
  Shield, 
  User,
  Loader2,
  Building2,
  Briefcase,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default role keys (will be overridden by company custom names)
const DEFAULT_ROLLEN = [
  { key: "fuehrungskraft", label: "Führungskraft (Abteilungsleiter)" },
  { key: "geschaeftsfuehrung", label: "Geschäftsführung" },
  { key: "personalabteilung", label: "Personalabteilung" },
  { key: "betriebsarzt", label: "Betriebsarzt" },
  { key: "operator", label: "Operator" }
];

export default function UserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    company_id: "",
    is_admin: false,
    abteilung: "",
    rolle: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Update available departments and roles when company changes
  useEffect(() => {
    if (newUser.company_id) {
      const company = companies.find(c => c.id === newUser.company_id);
      if (company) {
        // Set departments
        if (company.abteilungen && company.abteilungen.length > 0) {
          setAvailableDepartments(company.abteilungen);
        } else {
          setAvailableDepartments([]);
        }
        // Set roles with custom names
        const roleNames = company.custom_role_names || {};
        const defaultRoles = {
          fuehrungskraft: "Führungskraft",
          geschaeftsfuehrung: "Geschäftsführung",
          personalabteilung: "Personalabteilung",
          betriebsarzt: "Betriebsarzt",
          operator: "Operator"
        };
        setAvailableRoles(Object.entries(defaultRoles).map(([key, defaultName]) => ({
          key,
          name: roleNames[key] || defaultName
        })));
      }
      // Reset abteilung when company changes
      setNewUser(prev => ({ ...prev, abteilung: "", rolle: "" }));
    } else {
      setAvailableDepartments([]);
      setAvailableRoles([]);
    }
  }, [newUser.company_id, companies]);

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        axios.get(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get role display name
  const getRoleDisplayName = (roleKey, companyId) => {
    if (!roleKey) return null;
    
    const company = companies.find(c => c.id === companyId);
    if (company && company.custom_role_names && company.custom_role_names[roleKey]) {
      return company.custom_role_names[roleKey];
    }
    
    // Fallback to default names
    const defaults = {
      fuehrungskraft: "Führungskraft",
      geschaeftsfuehrung: "Geschäftsführung",
      personalabteilung: "Personalabteilung",
      betriebsarzt: "Betriebsarzt",
      operator: "Operator"
    };
    return defaults[roleKey] || roleKey;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    
    if (!newUser.company_id && !newUser.is_admin) {
      toast.error("Bitte ein Unternehmen auswählen oder Admin-Rechte vergeben");
      return;
    }

    // Validate role and department for company users
    if (newUser.company_id && !newUser.rolle) {
      toast.error("Bitte eine Rolle auswählen");
      return;
    }

    if (newUser.company_id && newUser.rolle === "Führungskraft" && !newUser.abteilung) {
      toast.error("Führungskräfte müssen einer Abteilung zugeordnet werden");
      return;
    }
    
    setCreating(true);
    
    try {
      const payload = {
        ...newUser,
        company_id: newUser.company_id || null,
        abteilung: newUser.abteilung || null,
        rolle: newUser.rolle || null
      };
      
      await axios.post(`${API}/auth/users`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Benutzer erstellt");
      setDialogOpen(false);
      setNewUser({ email: "", password: "", name: "", company_id: "", is_admin: false, abteilung: "", rolle: "" });
      fetchData();
    } catch (error) {
      console.error("Error creating user:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Fehler beim Erstellen des Benutzers");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} wurde gelöscht`);
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Fehler beim Löschen des Benutzers");
      }
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    
    setResettingPassword(true);
    
    try {
      await axios.post(`${API}/auth/users/${selectedUser.id}/reset-password`, 
        { user_id: selectedUser.id, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Passwort für ${selectedUser.name} wurde zurückgesetzt`);
      setResetDialogOpen(false);
      setNewPassword("");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Fehler beim Zurücksetzen des Passworts");
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="user-management-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-500 mb-2">ADMINISTRATION</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Benutzerverwaltung
          </h1>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="create-user-button"
              className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuer Benutzer
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">Neuen Benutzer erstellen</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Fügen Sie einen neuen Benutzer hinzu und weisen Sie ihm ein Unternehmen zu.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                  Name *
                </Label>
                <Input
                  id="name"
                  data-testid="new-user-name"
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Max Mustermann"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="new-user-email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@firma.de"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                  Passwort *
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="new-user-password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                  Unternehmen
                </Label>
                <Select 
                  value={newUser.company_id || "none"} 
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, company_id: value === "none" ? "" : value }))}
                >
                  <SelectTrigger 
                    id="company"
                    data-testid="new-user-company"
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  >
                    <SelectValue placeholder="Unternehmen wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="none" className="text-zinc-400">
                      Kein Unternehmen (Super-Admin)
                    </SelectItem>
                    {companies.map(company => (
                      <SelectItem 
                        key={company.id} 
                        value={company.id}
                        className="text-zinc-50 focus:bg-yellow-500/10 focus:text-yellow-500"
                      >
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {companies.length === 0 && (
                  <p className="text-xs text-yellow-500">
                    Hinweis: Erstellen Sie erst ein Unternehmen unter &quot;Unternehmen&quot;
                  </p>
                )}
              </div>

              {/* Role Selection - only show for company users */}
              {newUser.company_id && (
                <div className="space-y-2">
                  <Label htmlFor="rolle" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                    Rolle *
                  </Label>
                  <Select 
                    value={newUser.rolle || "none"} 
                    onValueChange={(value) => setNewUser(prev => ({ ...prev, rolle: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger 
                      id="rolle"
                      data-testid="new-user-rolle"
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    >
                      <SelectValue placeholder="Rolle wählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="none" className="text-zinc-400">
                        Rolle wählen...
                      </SelectItem>
                      {availableRoles.map(rolle => (
                        <SelectItem 
                          key={rolle.key} 
                          value={rolle.key}
                          className="text-zinc-50 focus:bg-yellow-500/10 focus:text-yellow-500"
                        >
                          {rolle.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Bestimmt welche E-Mail-Benachrichtigungen der Benutzer erhält
                  </p>
                </div>
              )}

              {/* Department Selection - only show for Führungskraft */}
              {newUser.company_id && newUser.rolle === "fuehrungskraft" && (
                <div className="space-y-2">
                  <Label htmlFor="abteilung" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                    Abteilung *
                  </Label>
                  <Select 
                    value={newUser.abteilung || "none"} 
                    onValueChange={(value) => setNewUser(prev => ({ ...prev, abteilung: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger 
                      id="abteilung"
                      data-testid="new-user-abteilung"
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    >
                      <SelectValue placeholder="Abteilung wählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="none" className="text-zinc-400">
                        Abteilung wählen...
                      </SelectItem>
                      {availableDepartments.map(abt => (
                        <SelectItem 
                          key={abt} 
                          value={abt}
                          className="text-zinc-50 focus:bg-yellow-500/10 focus:text-yellow-500"
                        >
                          {abt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availableDepartments.length === 0 && (
                    <p className="text-xs text-yellow-500">
                      Hinweis: Fügen Sie erst Abteilungen beim Unternehmen hinzu
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex items-center justify-between p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div>
                  <Label htmlFor="is_admin" className="text-sm text-zinc-300 cursor-pointer">
                    Super-Admin Rechte
                  </Label>
                  <p className="text-xs text-zinc-500">Kann alle Unternehmen und Benutzer verwalten</p>
                </div>
                <Switch
                  id="is_admin"
                  data-testid="new-user-admin"
                  checked={newUser.is_admin}
                  onCheckedChange={(checked) => setNewUser(prev => ({ ...prev, is_admin: checked }))}
                  className="data-[state=checked]:bg-yellow-500"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  data-testid="submit-new-user"
                  className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Erstellen...
                    </>
                  ) : (
                    "Erstellen"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <div className="grid gap-4">
        {users.map((user) => (
          <div 
            key={user.id}
            data-testid={`user-${user.id}`}
            className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${user.is_admin && !user.company_id ? 'bg-yellow-500/20' : 'bg-zinc-800'}`}>
                {user.is_admin && !user.company_id ? (
                  <Shield className="w-5 h-5 text-yellow-500" />
                ) : (
                  <User className="w-5 h-5 text-zinc-400" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-zinc-50">{user.name}</span>
                  {user.is_admin && !user.company_id && (
                    <span className="badge-warning text-xs px-2 py-0.5 rounded-sm">Super-Admin</span>
                  )}
                  {user.rolle && (
                    <span className="text-xs bg-zinc-800 text-yellow-500 px-2 py-0.5 rounded-sm flex items-center gap-1">
                      <Briefcase className="w-3 h-3" />
                      {getRoleDisplayName(user.rolle, user.company_id)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-zinc-500 flex-wrap">
                  <span>{user.email}</span>
                  {user.company_name && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {user.company_name}
                      </span>
                    </>
                  )}
                  {user.abteilung && (
                    <>
                      <span>•</span>
                      <span className="text-yellow-500/70">{user.abteilung}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => openResetDialog(user)}
                data-testid={`reset-password-${user.id}`}
                className="text-zinc-500 hover:text-yellow-500 hover:bg-yellow-500/10"
                title="Passwort zurücksetzen"
              >
                <Key className="w-4 h-4" />
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    data-testid={`delete-user-${user.id}`}
                    className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-zinc-50">Benutzer löschen?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Möchten Sie {user.name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                      Abbrechen
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleDeleteUser(user.id, user.name)}
                      className="bg-red-500 text-white hover:bg-red-600"
                    >
                      Löschen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        
        {users.length === 0 && (
          <div className="text-center py-12 border border-zinc-800 rounded-sm bg-zinc-900/50">
            <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-500">Keine Benutzer vorhanden</p>
          </div>
        )}
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              Passwort zurücksetzen: {selectedUser?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Setzen Sie ein neues Passwort für diesen Benutzer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                Neues Passwort
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                data-testid="admin-reset-password-input"
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setResetDialogOpen(false)}
                className="border-zinc-700 text-zinc-300"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={resettingPassword}
                data-testid="admin-reset-password-submit"
                className="bg-yellow-500 text-black hover:bg-yellow-400"
              >
                {resettingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  "Passwort setzen"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
